import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@codes/ui-kit/components/ui/box-accordion';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import {
    ArrowDown01,
    ArrowDown10,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    FileText,
    Home,
    Plus,
    Search,
    SquarePen,
    Star,
} from 'lucide-react';

import { BoxBlackLogo1, BoxPurpleLogo1 } from '@codes/assets';
import { useContents } from '@codes/box-contents';
import { Loader } from '@codes/shared';

import { SearchDialog } from './SearchDialog';
import { useCreateChildContentWithCache, useCreateContentWithCache } from '../hooks';

import type { ContentView } from '@lemoncloud/eureka-contents-api';

const MAX_CONTENT_DEPTH = 2;

type SortOrder = 'desc' | 'asc';

const SORT_ORDER = {
    DESC: 'desc' as const,
    ASC: 'asc' as const,
} as const;

const getSortLabels = (t: (key: string) => string) => ({
    desc: {
        icon: ArrowDown01,
        text: t('box:sidebar.sort.newest'),
        tooltip: t('box:sidebar.sort.tooltipToOldest'),
    },
    asc: {
        icon: ArrowDown10,
        text: t('box:sidebar.sort.oldest'),
        tooltip: t('box:sidebar.sort.tooltipToNewest'),
    },
});

const SortButton = ({
    sortOrder,
    onToggle,
    ariaLabel,
    t,
}: {
    sortOrder: SortOrder;
    onToggle: () => void;
    ariaLabel?: string;
    t: (key: string) => string;
}) => {
    const sortLabels = getSortLabels(t);
    const config = sortLabels[sortOrder];
    const IconComponent = config.icon;

    return (
        <button
            className="flex items-center gap-1 text-xs text-text-500 hover:text-text-700 transition-colors cursor-pointer"
            onClick={onToggle}
            title={config.tooltip}
            aria-label={ariaLabel || config.tooltip}
            type="button"
        >
            <IconComponent className="h-3 w-3" />
            <span>{config.text}</span>
        </button>
    );
};

const sortContentsByDate = <T extends { updatedAt?: string | null }>(contents: T[], order: SortOrder): T[] => {
    return [...contents].sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return order === SORT_ORDER.DESC ? dateB - dateA : dateA - dateB;
    });
};

export type ContentViewWithChildren = ContentView & {
    hasChild?: boolean;
    children?: ContentViewWithChildren[];
};

type SideBarProps = {
    currentContentTitle?: string;
    setSidebarOpen: (open: boolean) => void;
};

const ContentItem = ({
    content,
    level = 0,
    expandedItems,
    ...props
}: {
    content: ContentViewWithChildren;
    level?: number;
    currentContentId?: string;
    currentContentTitle?: string;
    onContentClick: (content: ContentView) => void;
    onCreateChildContentClick: (content: ContentView) => void;
    expandedItems: string[];
}) => (
    <AccordionItem value={`${content.id}-${level}`} key={content.id} className="border-none">
        <AccordionTrigger
            className={`group flex items-center justify-between w-full px-2 my-[1px] ${
                content.id === props.currentContentId ? 'bg-box-sidebar-hover text-text font-medium' : ''
            }`}
        >
            <button
                className="flex items-center"
                onClick={e => {
                    e.stopPropagation();
                    if (content.hasChild) {
                        const itemValue = `${content.id}-${level}`;
                        const isExpanded = expandedItems.includes(itemValue);
                        const newExpandedItems = isExpanded
                            ? expandedItems.filter(item => item !== itemValue)
                            : [...expandedItems, itemValue];
                        props.onExpandChange?.(newExpandedItems);
                    }
                }}
            >
                <ChevronRight
                    className={`h-4 w-4 shrink-0 text-text-700 transition-transform duration-200 group-data-[state=open]:rotate-90 ${
                        content.hasChild ? '' : 'opacity-0'
                    }`}
                />
            </button>
            <div
                className="flex-1 flex items-center gap-1 min-w-0"
                onClick={e => {
                    e.stopPropagation();
                    props.onContentClick(content);
                }}
            >
                <FileText className="h-4 w-4 shrink-0" />
                <div className="w-0 flex-1 truncate">
                    {content.id === props.currentContentId ? props.currentContentTitle : content.title}
                </div>
                {content.$activity?.isMark && (
                    <div className="relative">
                        <Star className="w-4 h-4 fill-[#FFC609] text-[#FFC609] group-hover:invisible" />
                        {level < MAX_CONTENT_DEPTH && (
                            <button
                                className="absolute top-0 left-0 invisible group-hover:visible"
                                onClick={e => {
                                    e.stopPropagation();
                                    props.onCreateChildContentClick(content);
                                }}
                            >
                                <Plus className="h-4 w-4 text-text-700" />
                            </button>
                        )}
                    </div>
                )}
            </div>
            {!content.$activity?.isMark && level < MAX_CONTENT_DEPTH && (
                <button
                    onClick={e => {
                        e.stopPropagation();
                        props.onCreateChildContentClick(content);
                    }}
                >
                    <Plus className="h-4 w-4 text-text-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </button>
            )}
        </AccordionTrigger>
        {content.hasChild && content.children && content.children.length > 0 && (
            <AccordionContent className="w-full">
                <div className={`pl-${level > 0 ? '4' : '0'} w-full`}>
                    {content.children.map(child => (
                        <ContentItem
                            key={child.id}
                            content={child}
                            level={level + 1}
                            {...props}
                            expandedItems={expandedItems}
                        />
                    ))}
                </div>
            </AccordionContent>
        )}
    </AccordionItem>
);

const ContentList = (props: {
    currentContentTitle?: string;
    contents: ContentViewWithChildren[];
    currentContentId?: string;
    onContentClick: (content: ContentView) => void;
    onCreateChildContentClick: (content: ContentView) => void;
    expandedItems: string[];
    onExpandChange: (expandedItems: string[]) => void;
}) => (
    <div className="space-y-1 mt-1">
        <Accordion type="multiple" value={props.expandedItems} className="w-full">
            {props.contents.map(content => (
                <ContentItem key={content.id} content={content} {...props} />
            ))}
        </Accordion>
    </div>
);

export const SideBar = ({ currentContentTitle, setSidebarOpen }: SideBarProps) => {
    const { t } = useTranslation(['translation', 'box']);
    const navigate = useNavigate();
    const location = useLocation();
    const { contentId } = useParams<{ contentId: string }>();

    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [activeTitle, setActiveTitle] = useState(currentContentTitle);
    const [bookmarkSortOrder, setBookmarkSortOrder] = useState<SortOrder>(SORT_ORDER.DESC);
    const [pageSortOrder, setPageSortOrder] = useState<SortOrder>(SORT_ORDER.DESC);

    const { handleCreate, isPending: isCreatePending } = useCreateContentWithCache();
    const { handleCreateChild, isPending: isCreateChildPending } = useCreateChildContentWithCache();
    const { data: contentsData, isLoading } = useContents({ limit: -1, activity: 1 });

    const allContents = useMemo(() => contentsData?.data || [], [contentsData]);
    const contentsWithChildren = useMemo(() => {
        const contentMap = new Map<string, ContentView[]>();

        allContents.forEach(content => {
            if (content.parentId) {
                if (!contentMap.has(content.parentId)) {
                    contentMap.set(content.parentId, []);
                }
                contentMap.get(content.parentId)?.push(content);
            }
        });

        const buildContentTree = (content: ContentView): ContentViewWithChildren => {
            const children = contentMap.get(content.id) || [];
            return {
                ...content,
                hasChild: contentMap.has(content.id),
                children: children.map(child => buildContentTree(child)),
            };
        };

        return allContents.filter(content => !content.parentId).map(content => buildContentTree(content));
    }, [allContents]);

    // 메모화된 정렬 함수들
    const toggleBookmarkSort = useCallback(() => {
        setBookmarkSortOrder(prev => (prev === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC));
    }, []);

    const togglePageSort = useCallback(() => {
        setPageSortOrder(prev => (prev === SORT_ORDER.DESC ? SORT_ORDER.ASC : SORT_ORDER.DESC));
    }, []);

    // 메모화된 컨텐츠 리스트
    const contents = useMemo(() => {
        const filtered = contentsWithChildren.filter(content => !content.$activity?.isMark && !content.parentId);
        return sortContentsByDate(filtered, pageSortOrder);
    }, [contentsWithChildren, pageSortOrder]);

    const bookmarkedContents = useMemo(() => {
        const filtered = contentsWithChildren.filter(content => content.$activity?.isMark && !content.parentId);
        return sortContentsByDate(filtered, bookmarkSortOrder);
    }, [contentsWithChildren, bookmarkSortOrder]);

    useEffect(() => {
        setActiveTitle(currentContentTitle);
    }, [currentContentTitle]);

    useEffect(() => {
        if (contentId && allContents.length > 0) {
            const content = allContents.find(c => c.id === contentId);
            if (content?.parentId) {
                const expandedSet = new Set<string>();

                const findParentPath = (currentId: string) => {
                    const current = allContents.find(c => c.id === currentId);
                    if (!current) {
                        return [];
                    }

                    if (!current.parentId) {
                        return [current.id];
                    }

                    return [...findParentPath(current.parentId), current.id];
                };

                const parentPath = findParentPath(content.parentId);

                parentPath.forEach((id, index) => {
                    expandedSet.add(`${id}-${index}`);
                });

                setExpandedItems(Array.from(expandedSet));
            }
        }
    }, [contentId, allContents]);

    const handleContentClick = (content: ContentView) => {
        if (content.id === contentId) {
            return;
        }
        setActiveTitle(content.title);
        navigate(`/box/${content.id}`);
    };

    const handleContentSelect = (content: ContentView) => {
        if (!content || !content.id) {
            toast({ description: t('box:sidebar.toast.noContent'), variant: 'destructive' });
            return;
        }
        navigate(`/box/${content.id}`);
    };

    const handleCreateChildContent = (parent: ContentView) => {
        if (!parent || !parent.id) {
            return;
        }
        console.log(parent);
        handleCreateChild(parent.id);
    };

    const handleExpandChange = (newExpandedItems: string[]) => {
        setExpandedItems(newExpandedItems);
    };

    const isHomePage = location.pathname === '/box/home';

    return (
        <div className="w-[248px] flex flex-col h-screen bg-[#F4F5F5] dark:bg-[#222325]">
            {/* 심플한 헤더 */}
            <div className="h-[54px] px-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                    <Image className="h-[30px]" src={BoxBlackLogo1} darkSrc={BoxPurpleLogo1} />
                </div>
                <div className="flex items-center gap-[9px] text-text-800">
                    <button onClick={() => setSidebarOpen(false)} aria-label="Toggle Sidebar">
                        <ChevronsLeft className="h-[18px] w-[18px] hover:text-[#BABCC0]" />
                    </button>
                    <button aria-label="Edit" onClick={handleCreate}>
                        <SquarePen className="h-[18px] w-[18px] hover:text-[#BABCC0]" />
                    </button>
                </div>
            </div>

            <div className="px-2 pb-6 mt-1 flex-1 overflow-auto">
                {/* 심플한 네비게이션 링크 */}
                <button
                    className="w-full text-left px-2 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-box-sidebar-hover rounded-md transition-colors flex items-center gap-2 group"
                    onClick={() => navigate('/')}
                >
                    <ChevronLeft className="h-3.5 w-3.5 group-hover:-translate-x-0.5 transition-transform" />
                    Back to EurekaCodes
                </button>

                <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-text-700 px-2 gap-2 hover:bg-transparent"
                    onClick={() => setIsSearchOpen(true)}
                >
                    <Search className="h-4 w-4" />
                    {t('box:sidebar.search')}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    className={`w-full h-[29px] justify-start text-text-700 px-2 gap-2 hover:bg-box-sidebar-hover ${
                        isHomePage ? 'bg-box-sidebar-hover text-text font-medium' : ''
                    }`}
                    onClick={() => navigate('/box/home')}
                >
                    <Home className="h-4 w-4" />
                    {t('box:sidebar.home')}
                </Button>
                <div className="mt-[22px]">
                    <div className="flex items-center justify-between px-2 mb-1">
                        <h2 className="text-xs text-text-600 font-medium">{t('box:sidebar.sections.bookmark')}</h2>
                        <SortButton
                            sortOrder={bookmarkSortOrder}
                            onToggle={toggleBookmarkSort}
                            ariaLabel={t('box:sidebar.sort.ariaBookmark')}
                            t={t}
                        />
                    </div>
                    {bookmarkedContents.length === 0 ? (
                        <div className="p-4 pb-0 text-sm text-text-600">{t('box:sidebar.noBookmarks')}</div>
                    ) : (
                        <ContentList
                            currentContentTitle={activeTitle}
                            contents={bookmarkedContents}
                            currentContentId={contentId}
                            onContentClick={handleContentClick}
                            onCreateChildContentClick={handleCreateChildContent}
                            expandedItems={expandedItems}
                            onExpandChange={handleExpandChange}
                        />
                    )}
                </div>
                <div className="mt-[22px]">
                    <div className="flex items-center justify-between px-2 mb-1">
                        <h2 className="text-xs text-text-600 font-medium">{t('box:sidebar.sections.page')}</h2>
                        <SortButton
                            sortOrder={pageSortOrder}
                            onToggle={togglePageSort}
                            ariaLabel={t('box:sidebar.sort.ariaPage')}
                            t={t}
                        />
                    </div>
                    {isLoading && <Loader />}
                    {!isLoading && contents.length === 0 && (
                        <div className="p-4 pb-0 text-sm text-text-600">{t('box:sidebar.noPages')}</div>
                    )}
                    {!isLoading && (
                        <ContentList
                            currentContentTitle={activeTitle}
                            contents={contents}
                            currentContentId={contentId}
                            onContentClick={handleContentClick}
                            onCreateChildContentClick={handleCreateChildContent}
                            expandedItems={expandedItems}
                            onExpandChange={handleExpandChange}
                        />
                    )}
                    <div className="px-2 sticky bottom-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-[22px] bg-[#F4F5F5] dark:bg-[#3A3C40] dark:border-[#53555B] w-full h-[33px] text-text-800 border border-text-700 font-medium gap-[6px] hover:text-[#84888F] hover:border-[#84888F] hover:bg-transparent"
                            onClick={handleCreate}
                            disabled={isCreatePending}
                        >
                            <Plus className="h-4 w-4 text-text-800" />
                            {t('box:sidebar.newPage')}
                        </Button>
                    </div>
                </div>
            </div>
            <SearchDialog
                open={isSearchOpen}
                onOpenChange={setIsSearchOpen}
                onContentSelect={handleContentSelect}
                contentsWithChildren={contentsWithChildren}
            />
        </div>
    );
};
